import type { StripeExpressCheckoutElement, StripeExpressCheckoutElementConfirmEvent } from '@stripe/stripe-js';

/** How long a retired element waits for its wallet sheet to settle before it is destroyed anyway. */
export const RETIRED_EXPRESS_CHECKOUT_TTL_MS = 5 * 60_000;

/**
 * Keeps an Express Checkout Element that is unmounting under an open wallet sheet able to answer that
 * sheet. Stripe.js delivers the sheet's confirm to the element that opened it and throws "Unhandled
 * expressCheckout Element confirm event" when that element has no confirm listener left, which is
 * what react-stripe-js leaves behind when it destroys the element on unmount (SHOP-WEB-45: the
 * customer paid with the card on file after an Apple Pay sheet Stripe still held open, the checkout
 * navigated to the order, and the sheet's confirm arrived three seconds later).
 *
 * Call it before react-stripe-js's own cleanup runs (a layout-effect cleanup of a parent does). Its
 * destroy() is held back until the sheet settles: a confirm is refused, since the checkout that took
 * it is gone and must not place a second order, a cancel just lets go, and the element is destroyed
 * on whichever comes first, or after RETIRED_EXPRESS_CHECKOUT_TTL_MS.
 */
export default function retireExpressCheckoutElement(
  element: StripeExpressCheckoutElement,
  onLateConfirm: (event: StripeExpressCheckoutElementConfirmEvent) => void,
  ttlMs: number = RETIRED_EXPRESS_CHECKOUT_TTL_MS,
): void {
  const destroy = element.destroy.bind(element);
  let destroyed = false;

  const destroyOnce = () => {
    if (destroyed) {
      return;
    }

    destroyed = true;
    clearTimeout(timer);

    try {
      destroy();
    } catch {
      // Already destroyed with its Elements group: nothing left to release.
    }
  };

  element.destroy = () => undefined;

  element.on('confirm', (event) => {
    try {
      event.paymentFailed({ reason: 'fail' });
    } catch {
      // The element's frame is gone from the page; the sheet times out on its own.
    }

    onLateConfirm(event);
    destroyOnce();
  });
  element.on('cancel', destroyOnce);

  const timer = setTimeout(destroyOnce, ttlMs);
}
